/**
 * useCanonicalData - Fetches canonical financial data from ClaimService.
 *
 * Replaces the old syncClientUploads flow. Calls GET /api/v1/claims/{claimId}/canonical-data
 * which returns cache-aware canonical data rebuilt from uploaded_files when stale.
 *
 * The backend returns financial_data (year-keyed aggregates) and tax1065Files
 * already built — NO frontend data transformation needed.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../../../../utils/api';

// Stable empty reference so `periods || EMPTY_ARRAY` doesn't create a new
// identity on every render (which would cause every child component that
// destructures `periods` to re-render).
const EMPTY_ARRAY: any[] = [];

interface UseCanonicalDataResult {
  canonicalData: any | null;
  periods: any[];
  financialData: Record<string, any[]> | null;
  tax1065Files: any[];
  calculatedYears: number[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useCanonicalData(claimId: string | null): UseCanonicalDataResult {
  const [canonicalData, setCanonicalData] = useState<any | null>(null);
  const [financialData, setFinancialData] = useState<Record<string, any[]> | null>(null);
  const [tax1065Files, setTax1065Files] = useState<any[]>([]);
  const [calculatedYears, setCalculatedYears] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);

  const fetchCanonical = useCallback(async () => {
    if (!claimId) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.get(`/api/v1/claims/${claimId}/canonical-data`, { showErrorToast: false });
      if (result?.canonical_data) {
        setCanonicalData(result.canonical_data);
      }
      // Backend provides these pre-built — no frontend transformation needed
      if (result?.financial_data) {
        setFinancialData(result.financial_data);
      }
      if (result?.tax1065Files) {
        setTax1065Files(result.tax1065Files);
      }
      if (result?.calculated_years) {
        setCalculatedYears(result.calculated_years);
      }
    } catch (e: any) {
      // 404 is expected for new claims with no files yet — not an error.
      const msg = e?.message || '';
      const is404 = e?.status === 404 || msg.toLowerCase().includes('not found');
      if (!is404) {
        console.error('Failed to fetch canonical data:', e);
        setError(msg || 'Failed to fetch canonical data');
      }
    } finally {
      setIsLoading(false);
    }
  }, [claimId]);

  // Fetch on mount when claimId is available
  useEffect(() => {
    if (claimId && !fetchedRef.current) {
      fetchedRef.current = true;
      fetchCanonical();
    }
  }, [claimId, fetchCanonical]);

  const refetch = useCallback(async () => {
    await fetchCanonical();
  }, [fetchCanonical]);

  // Memoize periods so the array identity is stable across renders when the
  // underlying canonical_data.periods list hasn't changed.  Without this,
  // `canonicalData?.periods || []` produces a new array literal each render
  // and every consumer that uses `periods` as an effect dep or prop re-fires.
  const periods = useMemo<any[]>(() => canonicalData?.periods || EMPTY_ARRAY, [canonicalData]);

  return {
    canonicalData,
    periods,
    financialData,
    tax1065Files,
    calculatedYears,
    isLoading,
    error,
    refetch,
  };
}
